import { defineStore } from 'pinia';
import { mapAllPlansResponseToPlans, type Plan } from '~/models/Plan.model';
import type { GetAllPlansResponse } from '~/repository/modules/plan.module';

type State = {
  plans: Plan[];
};

export type PlanIdToProductNameMap = { [key: number]: string };
export type PlanIdToPacIdMap = { [key: number]: number };
export type PlanAllowanceToPlanIdMapForFilter = { [key: string]: number };

export const usePlanStore = defineStore('plansStore', {
  state: (): State => ({
    plans: [],
  }),
  actions: {
    processAndSetPlans(plansResponse: GetAllPlansResponse): void {
      const processedPlans = mapAllPlansResponseToPlans(plansResponse);
      this.plans = processedPlans;
    },
    selectPlanProductName(planId?: number, planShortName?: string): string {
      const fallbackName = planShortName || '';

      if (planId) {
        return this.planIdToProductNameMap[planId] || fallbackName;
      }

      return fallbackName;
    },
  },
  getters: {
    planAllowanceToPlanIdMapForFilter(
      state: State,
    ): PlanAllowanceToPlanIdMapForFilter {
      return state.plans
        .filter(
          (plan) =>
            plan.isFamilyPlan === false && !plan.productName.includes('BTL'),
        )
        .reduce((acc: PlanAllowanceToPlanIdMapForFilter, plan) => {
          const firstBundlePrice =
            !!plan.bundlePrices && plan.bundlePrices.length > 0
              ? plan.bundlePrices[0]
              : null;

          const planAllowance = firstBundlePrice
            ? plan.bundlePrices[0].Plan?.RegularDataAllowance ||
              plan.bundlePrices[0].Plan?.DataAllowance ||
              0
            : 0;

          const key =
            firstBundlePrice && firstBundlePrice.Plan?.IsStudent
              ? `student${planAllowance}`
              : `${planAllowance}`;

          acc[key] = plan.planId;
          return acc;
        }, {});
    },
    familyParentPlans(state: State): Plan[] {
      return state.plans.filter(
        (plan) => !plan.isFamilyPlan && plan.isFamilyAllowed,
      );
    },
    familyPlans(state: State): Plan[] {
      return state.plans.filter((plan) => plan.isFamilyPlan);
    },
    planIdToPacIdMap(state: State): PlanIdToPacIdMap {
      const newMap: PlanIdToPacIdMap = {};

      for (const plan of state.plans) {
        if (!newMap[plan.planId]) {
          newMap[plan.planId] = plan.productAttributeCombinationId;
        }
      }

      return newMap;
    },
    planIdToProductNameMap(state: State): PlanIdToProductNameMap {
      const newMap: PlanIdToProductNameMap = {};

      for (const plan of state.plans) {
        if (!newMap[plan.planId]) {
          newMap[plan.planId] = plan.productName;
        }
      }

      return newMap;
    },
    studentPlanIds(state: State): number[] {
      const allBundlePrices = state.plans.flatMap((plan) => plan.bundlePrices);
      const allStudentPlanBundlePrices = allBundlePrices.filter(
        (bundlePrice) => bundlePrice.Plan?.IsStudent === true,
      );

      const uniquePlanIdsSet = new Set(
        allStudentPlanBundlePrices.map(
          (bundlePrice) => bundlePrice.Plan?.PlanId,
        ),
      );

      const uniquePlanIdsArray = Array.from(uniquePlanIdsSet);
      const uniquePlanIds = uniquePlanIdsArray.filter(
        (planId) => planId !== undefined,
      );

      return uniquePlanIds;
    },
  },
});
